import { useState, type ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { conclaveApi, type EventGiveAskItem } from '@/lib/conclave-api';
import { giveAskApi, giveAskHeading, type CreateGiveAskPayload, type GiveAskType } from '@/lib/give-ask-api';

type Draft = {
  name: string;
  phone: string;
  email: string;
  businessName: string;
  category: { businessCategory: string; subCategory: string } | null;
  notes: string;
};

type Editing = { mode: 'new' } | { mode: 'edit'; id: string } | null;

const EMPTY_DRAFT: Draft = {
  name: '',
  phone: '',
  email: '',
  businessName: '',
  category: null,
  notes: '',
};

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

function draftFrom(item: EventGiveAskItem): Draft {
  return {
    name: (item.type === 'ASK' ? item.title : item.contactName) ?? '',
    phone: item.contactPhone ?? '',
    email: item.contactEmail ?? '',
    businessName: item.businessName ?? '',
    category: item.category
      ? { businessCategory: item.category, subCategory: item.subCategory ?? '' }
      : null,
    notes: item.description,
  };
}

function validateDraft(kind: GiveAskType, draft: Draft): string | null {
  if (kind === 'ASK' && draft.name.trim().length < 3) return 'Add what you are looking for';
  if (kind === 'GIVE' && !draft.name.trim() && !draft.businessName.trim()) {
    return 'Add a name or a business name';
  }
  if (draft.notes.trim().length < 3) return 'Add a short note';
  return null;
}

function payloadFrom(kind: GiveAskType, draft: Draft): Omit<CreateGiveAskPayload, 'type'> {
  const name = draft.name.trim();
  return {
    title: kind === 'ASK' ? name : undefined,
    description: draft.notes.trim(),
    businessName: draft.businessName.trim() || null,
    category: draft.category?.businessCategory || null,
    subCategory: draft.category?.subCategory || null,
    contactName: kind === 'GIVE' ? name || null : null,
    contactPhone: draft.phone.trim() || null,
    contactEmail: draft.email.trim() || null,
  };
}

export function EventGivesAsksEditor({
  eventId,
  kind,
  items,
  onChanged,
}: {
  eventId: string;
  kind: GiveAskType;
  items: EventGiveAskItem[];
  onChanged: () => Promise<void>;
}) {
  const [showOthers, setShowOthers] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [saving, setSaving] = useState(false);

  const label = kind === 'GIVE' ? 'Gives' : 'Asks';
  const noun = kind === 'GIVE' ? 'give' : 'ask';
  const Noun = kind === 'GIVE' ? 'Give' : 'Ask';
  const mine = items.filter((item) => item.type === kind);
  const enabled = mine.filter((item) => item.enabled);
  const others = mine.filter((item) => !item.enabled);

  const toggle = async (item: EventGiveAskItem, next: boolean) => {
    try {
      await conclaveApi.setEventGiveAsks(eventId, [{ giveAskId: item.id, enabled: next }]);
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not update this item'));
    }
  };

  const save = async (action: () => Promise<unknown>, success: string, fallback: string) => {
    setSaving(true);
    try {
      await action();
      toast.success(success);
      setEditing(null);
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, fallback));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: EventGiveAskItem) => {
    if (!window.confirm(`Delete "${giveAskHeading(item)}"? It will be removed from all communities and events.`)) {
      return;
    }
    try {
      await giveAskApi.deleteOwn(item.id);
      toast.success(`${Noun} deleted`);
      await onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not delete'));
    }
  };

  const entryForm = (
    key: string,
    draft: Draft,
    onSave: (draft: Draft) => void
  ) => (
    <EntryForm
      key={key}
      kind={kind}
      initial={draft}
      saving={saving}
      onCancel={() => setEditing(null)}
      onSave={onSave}
    />
  );

  const row = (item: EventGiveAskItem) => {
    if (editing?.mode === 'edit' && editing.id === item.id) {
      return (
        <li key={item.id}>
          {entryForm(item.id, draftFrom(item), (draft) => {
            const problem = validateDraft(kind, draft);
            if (problem) {
              toast.error(problem);
              return;
            }
            void save(
              () => giveAskApi.updateOwn(item.id, payloadFrom(kind, draft)),
              `${Noun} updated`,
              'Could not update'
            );
          })}
        </li>
      );
    }
    const heading = giveAskHeading(item);
    const summary = [
      item.businessName && item.businessName !== heading ? item.businessName : null,
      item.subCategory || item.category,
      item.description,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <li
        key={item.id}
        className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5 md:rounded-2xl md:px-4 md:py-3"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold">{heading}</p>
          {summary ? <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{summary}</p> : null}
          {!item.enabled ? <p className="mt-1 text-xs text-amber-700">Not shown in this event</p> : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Switch
            checked={item.enabled}
            onCheckedChange={(next) => void toggle(item, next)}
            aria-label={`Show ${heading} in this event`}
          />
          <Button type="button" size="sm" variant="outline" onClick={() => setEditing({ mode: 'edit', id: item.id })}>
            Edit
          </Button>
          <Button type="button" size="sm" variant="outline" className="text-destructive" onClick={() => void remove(item)}>
            Delete
          </Button>
        </div>
      </li>
    );
  };

  return (
    <section className="space-y-3">
      {enabled.length === 0 ? (
        <p className="text-sm text-muted-foreground">No {label.toLowerCase()} enabled for this event yet.</p>
      ) : (
        <ul className="space-y-2">{enabled.map(row)}</ul>
      )}

      {showOthers ? (
        others.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other {label.toLowerCase()} to show.</p>
        ) : (
          <ul className="space-y-2">{others.map(row)}</ul>
        )
      ) : null}

      {editing?.mode === 'new' ? (
        <ul>
          <li>
            {entryForm('new', EMPTY_DRAFT, (draft) => {
              const problem = validateDraft(kind, draft);
              if (problem) {
                toast.error(problem);
                return;
              }
              void save(
                () => conclaveApi.createEventGiveAsk(eventId, { type: kind, ...payloadFrom(kind, draft) }),
                `${Noun} added and enabled for this event`,
                'Could not save'
              );
            })}
          </li>
        </ul>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {others.length > 0 ? (
          <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => setShowOthers((v) => !v)}>
            {showOthers ? 'Hide other options' : 'See more options'}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-xl"
          onClick={() => setEditing(editing?.mode === 'new' ? null : { mode: 'new' })}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add new {noun}
        </Button>
      </div>
    </section>
  );
}

function EntryForm({
  kind,
  initial,
  saving,
  onCancel,
  onSave,
}: {
  kind: GiveAskType;
  initial: Draft;
  saving: boolean;
  onCancel: () => void;
  onSave: (draft: Draft) => void;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="grid gap-3 rounded-xl border border-border bg-card p-3 md:grid-cols-2 md:rounded-2xl md:p-4">
      <Field label={kind === 'ASK' ? 'What you are looking for' : 'Name'} className="md:col-span-2">
        <Input value={draft.name} onChange={(e) => set('name', e.target.value)} />
      </Field>
      <Field label="Phone">
        <PhoneInput
          value={draft.phone || undefined}
          onChange={(v) => set('phone', v ?? '')}
          placeholder="Enter phone number"
          defaultCountry="IN"
        />
      </Field>
      <Field label="Email">
        <Input type="email" value={draft.email} onChange={(e) => set('email', e.target.value)} />
      </Field>
      <Field label="Business name">
        <Input value={draft.businessName} onChange={(e) => set('businessName', e.target.value)} />
      </Field>
      <Field label="Category" className="md:col-span-2">
        <BusinessCategoryPicker
          businessCategory={draft.category?.businessCategory}
          subCategory={draft.category?.subCategory}
          placeholder="Search category…"
          onSelect={(value) => set('category', value ?? null)}
        />
      </Field>
      <Field label="Notes" className="md:col-span-2">
        <Textarea
          value={draft.notes}
          onChange={(e) => set('notes', e.target.value)}
          maxLength={2000}
          className="min-h-24"
          placeholder="Any extra details…"
        />
      </Field>
      <div className="flex justify-end gap-2 md:col-span-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" disabled={saving} onClick={() => onSave(draft)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
    </label>
  );
}
